import { css } from '@emotion/react'
import {
  Button,
  Checkbox,
  HFlow,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalProps,
  Text,
  Theme,
  useTheme,
  VFlow,
} from 'bold-ui'
import { Box } from 'components/Box'
import { RequiredLabel } from 'components/form/core/RequiredLabel/RequiredLabel'
import { HLabel } from 'components/HLabel'
import { useEffect, useMemo, useRef, useState } from 'react'
import { mediaMobile } from 'util/breakpoints'

import { HideOnScreenSize, ResponsiveModal } from './responsive'

export interface Termo {
  titulo: string
  subtitulo?: string
  itens: ReadonlyArray<string>
  acceptText: string
}
export type TermosRecord<TTiposTermos extends string | number | symbol> = Record<TTiposTermos, Termo>

interface TermoModalProps<TTiposTermos extends string | number | symbol> extends ModalProps {
  onAccept?(autorizacoes: Record<TTiposTermos, boolean>): void
  termos: TermosRecord<TTiposTermos>
  nextText?: string
  prevText?: string
  confirmText?: string
  readonly?: boolean
}

export function TermoModal<TTiposTermos extends string | number | symbol>(props: TermoModalProps<TTiposTermos>) {
  const {
    open,
    onClose,
    onAccept,
    termos,
    nextText = 'Continuar',
    prevText = 'Anterior',
    confirmText = 'Aceitar termos',
    style: externalStyles,
    readonly = false,
    ...rest
  } = props

  const totalPaginas = Object.keys(termos).length - 1
  const paginasTermo = useMemo(() => Object.keys(termos) as TTiposTermos[], [termos])

  const [indexPaginaAtual, setIndexPaginaAtual] = useState(0)

  const paginaAtual = paginasTermo[indexPaginaAtual]
  const termoPaginaAtual = termos[paginaAtual]

  const theme = useTheme()
  const styles = createStyles(theme)

  const initialCheckboxValues = useMemo(
    () =>
      paginasTermo.reduce((acc, cur) => {
        acc[cur] = false
        return acc
      }, {} as Record<TTiposTermos, boolean>),
    [paginasTermo]
  )
  const [checkboxValues, setCheckboxValues] = useState(initialCheckboxValues)

  const refTermos = useRef<HTMLDivElement>()

  const closeModal = () => {
    setIndexPaginaAtual(0)
    onClose?.()
  }

  const nextPageOrAccept = () => {
    if (indexPaginaAtual < totalPaginas) {
      setIndexPaginaAtual(indexPaginaAtual + 1)
      refTermos.current?.scrollIntoView()
    } else {
      setIndexPaginaAtual(0)
      onAccept?.(checkboxValues)
    }
  }

  const prevPage = () => {
    if (indexPaginaAtual > 0) {
      setIndexPaginaAtual(indexPaginaAtual - 1)
      refTermos.current?.scrollIntoView()
    }
  }

  useEffect(() => {
    !open && setCheckboxValues(initialCheckboxValues)
  }, [initialCheckboxValues, open, termos])

  return (
    <ResponsiveModal
      open={open}
      onClose={closeModal}
      size='large'
      style={css(styles.modal, externalStyles)}
      wrapperStyle={styles.wrapper}
      manageOverflow
      scroll='full'
      {...rest}
    >
      <ModalHeader
        title={termoPaginaAtual.titulo}
        subtitle={
          <Text variant='h2' fontWeight='bold'>
            {termoPaginaAtual.subtitulo}
          </Text>
        }
      />
      <ModalBody style={[styles.scrollTerm, styles.scrollTermMobile]}>
        <VFlow vSpacing={1}>
          <VFlow vSpacing={1}>
            {termoPaginaAtual.itens?.map((termo, index) => (
              <HLabel title={`${index + 1} -`} key={index}>
                {termo}
              </HLabel>
            ))}
            {!readonly && (
              <Box>
                <Checkbox
                  onChange={() =>
                    setCheckboxValues((prev) => {
                      return { ...prev, [paginaAtual]: !checkboxValues[paginaAtual] }
                    })
                  }
                  checked={checkboxValues[paginaAtual]}
                  label={<RequiredLabel inline fontWeight='bold' label={termoPaginaAtual.acceptText} />}
                />
              </Box>
            )}
          </VFlow>
        </VFlow>
      </ModalBody>
      {(!readonly || totalPaginas > 1) && (
        <ModalFooter style={[styles.modalFooter, styles.modalFooterMobile]}>
          <HFlow justifyContent='flex-end' alignItems='center' style={styles.gridFooterMobile}>
            {totalPaginas > 0 && (
              <Text>
                <Text fontWeight='bold'>{indexPaginaAtual + 1} </Text>
                de
                <Text fontWeight='bold'> {totalPaginas + 1} </Text>
                termos obrigatórios
              </Text>
            )}
            {indexPaginaAtual > 0 && readonly && (
              <Button onClick={prevPage} kind='primary'>
                {prevText}
              </Button>
            )}
            <VFlow vSpacing={0.5} style={styles.textFooterMobile}>
              <HideOnScreenSize up='sm'>
                <Text fontStyle='italic'>É necessário concordar com o termo no final da tela para continuar.</Text>
              </HideOnScreenSize>
              {(indexPaginaAtual < totalPaginas || !readonly) && (
                <Button
                  disabled={!checkboxValues[paginaAtual]}
                  onClick={nextPageOrAccept}
                  kind='primary'
                  style={styles.buttonMobile}
                  data-testid='TermoModal.AceitarTermos'
                >
                  {indexPaginaAtual < totalPaginas ? nextText : confirmText}
                </Button>
              )}
            </VFlow>
          </HFlow>
        </ModalFooter>
      )}
    </ResponsiveModal>
  )
}

const createStyles = (theme: Theme) => ({
  scrollTerm: css`
    overflow-y: auto;
    width: 100%;
    padding-top: 0;
    flex: 1;
  `,
  modal: css`
    > div {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
    }
    ${mediaMobile} {
      overflow: hidden;
      > div {
        display: grid;
        grid-template-rows: auto 1fr auto;
        align-items: stretch;
      }
    }
  `,
  modalFooter: css`
    width: 100%;
  `,
  wrapper: css`
    padding: 0;
    width: 100%;
    height: 100%;
  `,
  scrollTermMobile: css`
    ${mediaMobile} {
      padding: 1rem;
      min-height: 0;
    }
  `,
  modalFooterMobile: css`
    ${mediaMobile} {
      border-top: 1px solid ${theme.pallete.divider};
      background-color: ${theme.pallete.gray.c100};
      grid-template: 1fr / 1fr;
      min-height: max-content;
    }
  `,
  gridFooterMobile: css`
    ${mediaMobile} {
      grid-template: 1fr / 1fr;
    }
  `,
  textFooterMobile: css`
    ${mediaMobile} {
      text-align: center;
    }
  `,
  buttonMobile: css`
    ${mediaMobile} {
      width: 100%;
    }
  `,
})
